import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiHeart } from "react-icons/fi";
import "../css/main.scss";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

const heroStories = [
  {
    image: "/img/editorial/home-hero-orchard.jpg",
    eyebrow: `${siteConfig.legalName} / Orchard series`,
    title: "Meet the Squareline Loafer",
    copy: "Original footwear shaped by craft, movement and the spaces between.",
    cta: "Shop now",
    to: "/shop?category=Loafers",
    position: "center",
  },
  {
    image: "/img/editorial/home-hero-summer.jpg",
    eyebrow: "New arrivals",
    title: "Discover the high summer drop",
    copy: "Quiet colour, honest materials and silhouettes made to move.",
    cta: "Shop new arrivals",
    to: "/shop?category=New%20Arrivals",
    position: "center",
  },
  {
    image: "/img/editorial/home-hero-sale.jpg",
    eyebrow: "Summer favourites",
    title: "Up to 50% off selected styles",
    copy: "Past-season pieces, ready for the next chapter.",
    cta: "Shop sale",
    to: "/shop?category=Sale",
    position: "center",
  },
];

const editorialStories = [
  { image: "/img/editorial/home-story-community.jpg", title: "Community", label: "Uncommon stories", to: "/about" },
  { image: "/img/editorial/home-story-craft.jpg", title: "Made with purpose", label: "Quality & craftsmanship", to: "/about" },
  { image: "/img/editorial/home-story-motion.jpg", title: "Our world", label: "Culture in motion", to: "/about" },
  { image: "/img/editorial/home-story-together.jpg", title: "United spirit", label: "Rewards & community", to: "#newsletter" },
];

function Main() {
  const cart = useContext(CartContext);
  const saleProducts = productData.filter((product) => product.compareAtPrice).slice(0, 8);
  const isWishlisted = cart.isWishlisted || cart.isInWishlist || (() => false);
  const toggleWishlist = cart.toggleWishlist || (() => {});

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="main-page">
      <main id="main-content" tabIndex="-1">
        {heroStories.map((story, index) => (
          <section className={`home-hero home-hero--${index + 1}`} key={story.title} aria-labelledby={`hero-title-${index}`}>
            <img
              src={story.image}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
              style={{ objectPosition: story.position }}
            />
            <div className="home-hero__shade" aria-hidden="true" />
            <div className="home-hero__content">
              <p>{story.eyebrow}</p>
              {index === 0 ? (
                <h1 id={`hero-title-${index}`}>{story.title}</h1>
              ) : (
                <h2 id={`hero-title-${index}`}>{story.title}</h2>
              )}
              <span>{story.copy}</span>
              <Link to={story.to} onClick={scrollTop} className="home-hero__cta">
                {story.cta} <FiArrowRight aria-hidden="true" />
              </Link>
            </div>
          </section>
        ))}

        <section className="home-products" aria-labelledby="sale-heading">
          <div className="home-section-heading">
            <div>
              <p>Selected pieces</p>
              <h2 id="sale-heading">Summer sale</h2>
            </div>
            <Link to="/shop?category=Sale" onClick={scrollTop}>Shop all sale <FiArrowRight aria-hidden="true" /></Link>
          </div>

          <div className="home-product-grid">
            {saleProducts.map((product) => (
              <article className="home-product-card" key={product.id}>
                <Link className="home-product-card__image" to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                  <img src={product.image} alt={product.name} loading="lazy" />
                  <span className="home-product-card__status">{product.status}</span>
                </Link>
                <button
                  className={`home-product-card__wish ${isWishlisted(product.id) ? "is-active" : ""}`}
                  type="button"
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                >
                  <FiHeart aria-hidden="true" />
                </button>
                <Link className="home-product-card__meta" to={`/shopDetails/${product.id}`} onClick={scrollTop}>
                  <h3>{product.name}</h3>
                  <p>
                    {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
                    <span>{formatPrice(product.price)}</span>
                  </p>
                </Link>
              </article>
            ))}
          </div>
          <Link className="home-products__mobile-link" to="/shop?category=Sale" onClick={scrollTop}>Shop all sale</Link>
        </section>

        <section className="campaign-banner" aria-labelledby="campaign-title">
          <img src="/img/editorial/home-campaign.jpg" alt="Creative community gathered in an orchard courtyard" loading="lazy" />
          <div className="campaign-banner__content">
            <p>In collaboration with independent makers</p>
            <h2 id="campaign-title">Meet in the orchard</h2>
            <Link to="/about" onClick={scrollTop}>Discover the story <FiArrowRight aria-hidden="true" /></Link>
          </div>
        </section>

        <section className="editorial-grid" aria-label="Explore our world">
          {editorialStories.map((story) => {
            const cardContent = (
              <>
                <img src={story.image} alt="" loading="lazy" />
                <span>{story.label}</span>
                <h2>{story.title}</h2>
                <small>Discover <FiArrowRight aria-hidden="true" /></small>
              </>
            );

            return story.to.startsWith("#") ? (
              <a className="editorial-card" href={story.to} key={story.title}>
                {cardContent}
              </a>
            ) : (
              <Link className="editorial-card" to={story.to} key={story.title} onClick={scrollTop}>
                {cardContent}
              </Link>
            );
          })}
        </section>

        <section className="brand-mission" aria-labelledby="mission-title">
          <img src="/img/editorial/home-mission.jpg" alt="Two friends walking from an orchard toward modern architecture" loading="lazy" />
          <div className="brand-mission__content">
            <p>Community, culture & product</p>
            <h2 id="mission-title">Bridge the gap.</h2>
            <span>{siteConfig.description}</span>
            <Link to="/about" onClick={scrollTop}>About us <FiArrowRight aria-hidden="true" /></Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Main;
